import { ErrorBoundary } from 'react-error-boundary';
import { SubmitHandler } from 'react-hook-form';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as Avatar from '@givebutter/primitives/avatar';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { maskWholeNumber } from '@givebutter/util';
import { faSave, faSpinnerThird, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faFileCsv, faFilePdf } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LetterDownloadPreviewButton } from 'assets/js/pages/engage/letters/LetterDownloadPreviewButton';
import { LetterFlowNextButton } from 'assets/js/pages/engage/letters/LetterFlowNextButton';
import { LetterDraftFormValues } from 'assets/js/pages/engage/letters/types';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useLetterDraftSubmit } from 'assets/js/pages/engage/letters/useDraftSubmit';
import { useLetterMeta } from 'assets/js/pages/engage/letters/useLetterMeta';
import { useFormContext } from 'components/form';
import * as Stepper from 'components/stepper';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';

const LetterFormNavigation = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const { isBodyEmpty, noRecipientCount, isOnConfirm, isOnEditor, isEasySend } = useLetterMeta();
    const { isFetching, data } = useCurrentLetter();
    const { handleSubmit } = useFormContext<LetterDraftFormValues>();
    const { isPending: isSubmitting, mutate: submit } = useLetterDraftSubmit();

    return (
        <Stepper.Root>
            <div
                className={`flex items-center justify-between gap-6 border border-gray-200 bg-gray-50 px-4 pb-6 pt-4 sm:px-8 sm:pb-8 sm:pt-6 ${
                    isSubmitting ? 'pointer-events-none' : ''
                }`}
            >
                <div className={'sm:flex-1'}>
                    <Button
                        variant="none"
                        className="font-medium text-gray-500 hover:text-gray-700"
                        onClick={() => history.push(`/accounts/${accountId}/engage/letters`)}
                    >
                        <FontAwesomeIcon icon={faTimes} size="lg" />
                    </Button>
                </div>
                <div className={'flex flex-grow justify-center'}>
                    <div className={'w-full max-w-md'}>
                        <Stepper.Container>
                            <Stepper.Step
                                meta={{ path: `${match.url}/details` }}
                                value={'details'}
                            />
                            <Stepper.Step
                                meta={{ path: `${match.url}/compose` }}
                                value={'compose'}
                            />
                            <Stepper.Step
                                meta={{ path: `${match.url}/envelope` }}
                                value={'envelope'}
                                hidden={!isEasySend}
                            />
                            <Stepper.Step
                                meta={{ path: `${match.url}/filters` }}
                                value={'recipients'}
                            />
                            <Stepper.Step
                                meta={{ path: `${match.url}/personalize` }}
                                value={'personalize'}
                                hidden={!isEasySend}
                            />
                            <Stepper.Step
                                meta={{ path: `${match.url}/confirm` }}
                                value={'confirm'}
                            />
                        </Stepper.Container>
                    </div>
                </div>
                <div className={'flex justify-end gap-2 sm:flex-1'}>
                    {!isEasySend && (
                        <>
                            <ErrorBoundary fallback={null}>
                                {(isOnConfirm || isOnEditor) && <LetterDownloadPreviewButton />}
                            </ErrorBoundary>
                            {isOnConfirm && (
                                <Link to={`/accounts/${accountId}/engage/letters`}>
                                    <Button size="sm" variant="outline" color="neutral">
                                        <FontAwesomeIcon icon={faSave} /> Save as draft
                                    </Button>
                                </Link>
                            )}
                        </>
                    )}
                    {!isOnConfirm && <LetterFlowNextButton />}
                    {isOnConfirm && !isEasySend && (
                        <AlertDialog.Root type="warning">
                            <AlertDialog.Trigger asChild={true}>
                                <Button
                                    variant="solid"
                                    disabled={isFetching || isBodyEmpty || noRecipientCount}
                                    size={'sm'}
                                >
                                    Generate letters
                                </Button>
                            </AlertDialog.Trigger>
                            <AlertDialog.Portal>
                                <AlertDialog.Overlay />
                                <AlertDialog.Content className="max-w-[420px]">
                                    <AlertDialog.Header>
                                        <AlertDialog.Icon />
                                        <AlertDialog.Close disabled={isSubmitting} />
                                    </AlertDialog.Header>
                                    <AlertDialog.Body>
                                        <AlertDialog.Title>
                                            Ready to generate letters?
                                        </AlertDialog.Title>
                                        <div className={'flex flex-col gap-4'}>
                                            <AlertDialog.Description>
                                                This may take a few minutes. You can leave this page
                                                and return later. You'll be notified via email when
                                                the files are ready.
                                            </AlertDialog.Description>
                                            <div className={'mt-2 flex flex-col gap-4'}>
                                                <div className={'flex items-center gap-4'}>
                                                    <Avatar.Root>
                                                        <Avatar.Fallback aria-label="PDF file icon">
                                                            <FontAwesomeIcon
                                                                size={'lg'}
                                                                className={
                                                                    'text-brand-violet-primary'
                                                                }
                                                                icon={faFilePdf}
                                                            />
                                                        </Avatar.Fallback>
                                                    </Avatar.Root>
                                                    <div className={'flex flex-col'}>
                                                        <p className={'text-sm font-medium'}>
                                                            Letters (
                                                            {maskWholeNumber(
                                                                data?.recipient_count ?? 0,
                                                            )}
                                                            )
                                                        </p>
                                                        <p className={'text-sm text-gray-600'}>
                                                            PDF file of all letters
                                                        </p>
                                                    </div>
                                                </div>
                                                <Separator />
                                                <div className={'flex items-center gap-4'}>
                                                    <Avatar.Root>
                                                        <Avatar.Fallback aria-label="CSV file icon">
                                                            <FontAwesomeIcon
                                                                size={'lg'}
                                                                className={
                                                                    'text-brand-violet-primary'
                                                                }
                                                                icon={faFileCsv}
                                                            />
                                                        </Avatar.Fallback>
                                                    </Avatar.Root>
                                                    <div className={'flex flex-col'}>
                                                        <p className={'text-sm font-medium'}>
                                                            Names and addresses
                                                        </p>
                                                        <p className={'text-sm text-gray-600'}>
                                                            CSV file of all contact names and
                                                            addresses
                                                        </p>
                                                    </div>
                                                </div>
                                                <Separator />
                                                <div className={'flex items-center gap-4'}>
                                                    <Avatar.Root>
                                                        <Avatar.Fallback aria-label="PDF file icon">
                                                            <FontAwesomeIcon
                                                                size={'lg'}
                                                                className={
                                                                    'text-brand-violet-primary'
                                                                }
                                                                icon={faFilePdf}
                                                            />
                                                        </Avatar.Fallback>
                                                    </Avatar.Root>
                                                    <div className={'flex flex-col'}>
                                                        <p className={'text-sm font-medium'}>
                                                            Envelopes and labels
                                                        </p>
                                                        <p className={'text-sm text-gray-600'}>
                                                            PDF files of #10 envelopes and Avery
                                                            5160 labels
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AlertDialog.Body>
                                    <AlertDialog.Footer className={'mt-2 gap-4'}>
                                        <AlertDialog.Cancel
                                            disabled={isSubmitting}
                                            className={'flex-1'}
                                        >
                                            Cancel
                                        </AlertDialog.Cancel>
                                        <Button
                                            variant="solid"
                                            className={'w-1/2 flex-1'}
                                            disabled={
                                                isSubmitting ||
                                                isFetching ||
                                                isBodyEmpty ||
                                                noRecipientCount
                                            }
                                            onClick={() =>
                                                handleSubmit(
                                                    submit as SubmitHandler<LetterDraftFormValues>,
                                                )()
                                            }
                                        >
                                            <div className={'flex items-center gap-2'}>
                                                {isSubmitting && (
                                                    <FontAwesomeIcon
                                                        className={'animate-spin'}
                                                        icon={faSpinnerThird}
                                                    />
                                                )}
                                                <p>Confirm</p>
                                            </div>
                                        </Button>
                                    </AlertDialog.Footer>
                                </AlertDialog.Content>
                            </AlertDialog.Portal>
                        </AlertDialog.Root>
                    )}
                </div>
            </div>
        </Stepper.Root>
    );
};

export { LetterFormNavigation };
